import React from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  // Nav / tab icons (M1.3) — semantic-curated per decision #30.
  // All exports verified against @hugeicons/core-free-icons via probe.
  Robot02Icon,                  // tab.agents — was Users (generic) → Robot (AI agent)
  PuzzleIcon,                   // tab.builder — was Hexagon (abstract) → Puzzle (canvas-builder)
  Store04Icon,                  // tab.marketplace
  Radio01Icon,                  // tab.channels — verified (Broadcast not exported)
  DeliverySent01Icon,           // tab.campaigns — paper-plane "sent" metaphor
  Calendar03Icon,               // tab.schedules
  BinocularsIcon,               // tab.supervisor — oversight metaphor (Detective not exported)
  AnalyticsUpIcon,              // tab.analytics — trending up (vs static ChartBar)
  Activity03Icon,               // tab.observability — pulse/monitoring line (Observe family; distinct from supervisor Binoculars + analytics AnalyticsUp)
  ShieldUserIcon,               // tab.admin — admin power (vs generic Gear)
  WorkflowSquare01Icon,         // tab.workflow

  // Top bar / theme (M1.4)
  Sun03Icon, Moon01Icon,
  UserIcon, Logout02Icon,
  ArrowDown01Icon, ArrowUp01Icon, ArrowLeft01Icon, ArrowRight01Icon,
  ArrowLeft02Icon, ArrowRight02Icon,

  // Channel icons (M1.5)
  CallIcon, SmartPhone01Icon, WhatsappIcon, Chatting01Icon,
  Globe02Icon, Mail01Icon, PlugSocketIcon,

  // Status indicators (M1.6)
  CheckmarkCircle02Icon, Cancel01Icon, AlertCircleIcon,
  PauseIcon, PlayIcon, Alert02Icon, CircleIcon,

  // Common actions (M1.7)
  Add01Icon, Remove01Icon, Cancel02Icon, Tick02Icon,
  Search01Icon, FilterIcon, Delete02Icon, PencilEdit02Icon,
  Copy01Icon, Download01Icon, Upload01Icon,
  ArrowUpRight01Icon, ArrowDownRight01Icon,
  ArrowReloadHorizontalIcon, FloppyDiskIcon, Link01Icon,

  // Builder / canvas / data (M1.7)
  CubeIcon, Layers01Icon, Database01Icon, File01Icon, FolderIcon,
  Image01Icon, CodeIcon, FlashIcon, SparklesIcon, StarIcon,
  Bookmark01Icon, Tag01Icon,

  // Knowledge / docs
  Book01Icon, BookOpen01Icon, FileSearchIcon, LibrariesIcon,

  // Builder node icons (M1.8 — 2026-04-29 audit) — every Builder node
  // type gets a DEDICATED Hugeicons silhouette so no two palette items
  // share a glyph. Verified via probe of @hugeicons/core-free-icons@1.x.
  AiBrain05Icon,                // node.llm — distinct from Robot (agent)
  Shield01Icon, Shield02Icon,   // node.input_guard / node.output_guard
  GitBranchIcon,                // node.condition — branching metaphor
  Navigation03Icon,             // node.action — directional send (vs decorative arrow)
  ApiIcon,                      // node.api — generic HTTP API call
  ConnectIcon,                  // node.openapi — externally-bound integration
  Plug01Icon,                   // node.mcp — MCP plugs into Bifrost (vs channel.webhook)
  Compass01Icon,                // node.grounding — anchored navigation (Vertex AI Search grounding)
  Settings02Icon,               // node.preprocess — gear/cog (NLP pipeline config)
  FunctionIcon,                 // node.function — fx symbol
  ToolsIcon,                    // node.tools — toolbox/wrench
  Flowchart01Icon,              // node.workflow — distinct from tab.workflow square
  TestTubeIcon,                 // feature.tester — Platform Tester button (vs feature.zap)

  // Misc
  InformationCircleIcon, HelpCircleIcon, LockIcon,
  Notification01Icon, Clock01Icon,
} from "@hugeicons/core-free-icons";
import { COLORS } from "../theme/colors";

// I² Visual Identity v1, Modernization addendum M1.2 (2026-04-29).
// Decisions #29 + #30: Hugeicons Bulk style, semantic-curated NAME_MAP.
//
// Hugeicons Bulk is the closest legal web equivalent to iOS SF Symbols
// hierarchical / palette modes — filled-with-accent two-color icons.
// Used in production by mid-2024-era design-forward apps; closer to
// Apple's icon language than any pure-stroke library.
//
// API note (Hugeicons React v1.x): a single <HugeiconsIcon /> render
// component takes an `icon` prop pointing to an icon definition from
// @hugeicons/core-free-icons. This reduces tree-shake friction
// because only the explicitly-imported icon definitions ship; the
// renderer stays at a single ~1KB component.
//
//   <Icon name="tab.agents" size={22} />
//   <Icon name="status.live" />
//   <Icon name="action.delete" color={COLORS.pink} />
//
// Wrapper defaults: variant="bulk" (the iOS SF look), size=20,
// color=COLORS.text. strokeWidth normalized at 1.75.

const NAME_MAP = {
  // ── Tab / Nav (M1.3) — semantic-curated ───────────────────
  "tab.agents":      Robot02Icon,
  "tab.builder":     PuzzleIcon,
  "tab.marketplace": Store04Icon,
  "tab.channels":    Radio01Icon,
  "tab.campaigns":   DeliverySent01Icon,
  "tab.schedules":   Calendar03Icon,
  "tab.supervisor":  BinocularsIcon,
  "tab.analytics":   AnalyticsUpIcon,
  "tab.observability": Activity03Icon,
  "tab.admin":       ShieldUserIcon,
  "tab.skills":      SparklesIcon,
  "tab.workflow":    WorkflowSquare01Icon,

  // ── Top bar / theme (M1.4) ────────────────────────────────
  "theme.dark":      Moon01Icon,
  "theme.light":     Sun03Icon,
  "user.profile":    UserIcon,
  "user.signout":    Logout02Icon,
  "chevron.down":    ArrowDown01Icon,
  "chevron.up":      ArrowUp01Icon,
  "chevron.left":    ArrowLeft01Icon,
  "chevron.right":   ArrowRight01Icon,
  "chevrons.left":   ArrowLeft02Icon,
  "chevrons.right":  ArrowRight02Icon,

  // ── Channel icons (M1.5) ──────────────────────────────────
  "channel.voice":    CallIcon,
  "channel.sms":      SmartPhone01Icon,
  "channel.whatsapp": WhatsappIcon,
  "channel.chat":     Chatting01Icon,
  "channel.web":      Globe02Icon,
  "channel.email":    Mail01Icon,
  "channel.webhook":  PlugSocketIcon,

  // ── Status indicators (M1.6) ──────────────────────────────
  "status.live":     CheckmarkCircle02Icon,
  "status.paused":   PauseIcon,
  "status.draft":    CircleIcon,
  "status.ok":       CheckmarkCircle02Icon,
  "status.error":    Cancel01Icon,
  "status.warning":  Alert02Icon,
  "status.info":     AlertCircleIcon,
  "status.running":  PlayIcon,

  // ── Common actions (M1.7) ─────────────────────────────────
  "action.add":       Add01Icon,
  "action.remove":    Remove01Icon,
  "action.close":     Cancel02Icon,
  "action.confirm":   Tick02Icon,
  "action.search":    Search01Icon,
  "action.filter":    FilterIcon,
  "action.delete":    Delete02Icon,
  "action.edit":      PencilEdit02Icon,
  "action.duplicate": Copy01Icon,
  "action.refresh":   ArrowReloadHorizontalIcon,
  "action.download":  Download01Icon,
  "action.upload":    Upload01Icon,
  "action.external":  Link01Icon,
  "action.next":      ArrowRight01Icon,
  "action.prev":      ArrowLeft01Icon,
  "action.expand":    ArrowUpRight01Icon,
  "action.collapse":  ArrowDownRight01Icon,
  "action.undo":      ArrowReloadHorizontalIcon,
  "action.redo":      ArrowReloadHorizontalIcon,
  "action.save":      FloppyDiskIcon,

  // ── Builder / canvas / data (M1.7) ────────────────────────
  "block.box":      CubeIcon,
  "block.layers":   Layers01Icon,
  "block.database": Database01Icon,
  "block.file":     File01Icon,
  "block.folder":   FolderIcon,
  "block.image":    Image01Icon,
  "block.code":     CodeIcon,
  "block.bot":      Robot02Icon,
  "feature.zap":    FlashIcon,
  "feature.spark":  SparklesIcon,
  "feature.star":   StarIcon,
  "feature.bookmark": Bookmark01Icon,
  "feature.tag":    Tag01Icon,
  "feature.tester": TestTubeIcon,

  // ── Knowledge / docs ───────────────────────────────────────
  "kb.book":     Book01Icon,
  "kb.bookopen": BookOpen01Icon,
  "kb.search":   FileSearchIcon,
  "kb.library":  LibrariesIcon,

  // ── Builder node types (M1.8 — 2026-04-29 audit) ──────────
  // Every Builder palette node MUST have a unique iconName and a
  // unique underlying Hugeicons silhouette. Mapping is dedicated so
  // semantic icons never accidentally pull in tab.* / channel.* /
  // status.* glyphs from a different category.
  "node.trigger":      FlashIcon,        // entry point — lightning
  "node.llm":          AiBrain05Icon,    // distinct from agent (Robot)
  "node.knowledge":    Book01Icon,       // KB
  "node.input_guard":  Shield01Icon,     // generic security shield (vs admin's ShieldUser)
  "node.output_guard": Shield02Icon,     // shield variant for symmetry
  "node.condition":    GitBranchIcon,    // branching decision
  "node.action":       Navigation03Icon, // directional send / deliver
  "node.api":          ApiIcon,          // generic HTTP API
  "node.memory":       Database01Icon,   // ES/conversation history store
  "node.grounding":    Compass01Icon,    // anchored navigation
  "node.preprocess":   Settings02Icon,   // gear/cog — NLP pipeline config
  "node.function":     FunctionIcon,     // fx symbol
  "node.agent":        Robot02Icon,      // sub-agent — Robot (LLM gets the brain now)
  "node.tools":        ToolsIcon,        // internal toolbox
  "node.mcp":          Plug01Icon,       // MCP plugs in (vs channel.webhook)
  "node.openapi":      ConnectIcon,      // bound external service
  "node.workflow":     Flowchart01Icon,  // canvas workflow (vs tab.workflow square)

  // ── Misc ──────────────────────────────────────────────────
  "info":     InformationCircleIcon,
  "help":     HelpCircleIcon,
  "lock":     LockIcon,
  // "unlock" omitted — no UnlockedIcon export in @hugeicons/core-free-icons
  // v1.0.6. Add when upstream exposes one.
  "bell":     Notification01Icon,
  "clock":    Clock01Icon,
  "alert":    AlertCircleIcon,
};

/**
 * <Icon name size strokeWidth color aria-label />
 *
 * @param {string}        name         — semantic key from NAME_MAP
 * @param {number}        size         — px (default 20)
 * @param {number}        strokeWidth  — default 1.75
 * @param {string}        color        — CSS color string; default COLORS.text
 * @param {string}        aria-label   — required for non-decorative icons
 * @param {string}        title        — optional native tooltip
 * @param {object}        style        — extra style overrides
 * @param {string}        className    — extra className
 */
export default function Icon({
  name,
  size = 20,
  strokeWidth = 1.75,
  color,
  "aria-label": ariaLabel,
  title,
  style,
  className,
  ...rest
}) {
  const def = NAME_MAP[name];
  if (!def) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`<Icon name="${name}"> is not in NAME_MAP — falling back to CircleIcon.`);
    }
    return (
      <HugeiconsIcon
        icon={CircleIcon}
        size={size}
        strokeWidth={strokeWidth}
        color={color || COLORS.textDim}
        aria-label={ariaLabel}
        title={title}
        style={style}
        className={className}
        {...rest}
      />
    );
  }
  return (
    <HugeiconsIcon
      icon={def}
      size={size}
      strokeWidth={strokeWidth}
      color={color || COLORS.text}
      aria-label={ariaLabel}
      title={title}
      style={style}
      className={className}
      {...rest}
    />
  );
}

// Export the name list so tests / lint rules can verify completeness.
export const ICON_NAMES = Object.keys(NAME_MAP);
